import React, { useState, useEffect } from 'react';
import ReportTable, { Column } from '../ReportTable';
import { Property, Invoice, Transaction } from '../../types';

interface PropertyReportsProps {
  properties: Property[];
  invoices: Invoice[];
  transactions: Transaction[];
  initialReport?: string;
}

const PropertyReports: React.FC<PropertyReportsProps> = ({ properties, invoices, transactions, initialReport }) => {
  const [selectedReport, setSelectedReport] = useState<string>(initialReport || 'operations');
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');

  useEffect(() => {
    if (initialReport) {
      setSelectedReport(initialReport);
    }
  }, [initialReport]);

  // Filtering Logic
  const filteredProperties = properties.filter(p => {
    const propertyMatch = selectedPropertyId === 'All' || String(p.id) === String(selectedPropertyId);
    const statusMatch = statusFilter === 'All' || p.status === statusFilter;
    return propertyMatch && statusMatch;
  });

  const operationsData = filteredProperties.map(p => {
    const pExpenses = transactions.filter(exp => String(exp.propertyId) === String(p.id) && exp.type === 'Expense');
    const pIncome = transactions.filter(inc => String(inc.propertyId) === String(p.id) && inc.type === 'Income');
    
    const revenue = pIncome.reduce((sum, inc) => sum + Number(inc.amount), 0);
    const totalExp = pExpenses.reduce((sum, e) => sum + Number(e.amount), 0);
    
    return {
      propertyId: p.id,
      name: p.name,
      revenue,
      expenses: totalExp,
      net: revenue - totalExp,
      status: p.status
    };
  });

  const operationsColumns: Column[] = [
    { header: 'اسم العقار', accessor: 'name' },
    { header: 'حالة العقار', accessor: 'status' },
    { header: 'إجمالي الإيرادات', accessor: 'revenue', render: val => <span className="text-green-600 font-bold">{Number(val).toLocaleString()} د.ك</span> },
    { header: 'إجمالي المصروفات', accessor: 'expenses', render: val => <span className="text-red-600 font-bold">{Number(val).toLocaleString()} د.ك</span> },
    { header: 'الصافي', accessor: 'net', render: val => <span className={Number(val) >= 0 ? "text-fly-600 font-black" : "text-red-600 font-black"}>{Number(val).toLocaleString()} د.ك</span> }
  ];

  const detailedData = operationsData.map(d => ({
    ...d,
    ratio: d.revenue > 0 ? ((d.expenses / d.revenue) * 100).toFixed(1) + '%' : '100%',
    totalUnits: properties.find(p => String(p.id) === String(d.propertyId))?.units?.length || 0
  }));

  const detailedColumns: Column[] = [
    { header: 'اسم العقار', accessor: 'name' },
    { header: 'إجمالي الوحدات', accessor: 'totalUnits' },
    { header: 'الإيرادات', accessor: 'revenue', render: val => `${Number(val).toLocaleString()} د.ك` },
    { header: 'المصروفات', accessor: 'expenses', render: val => `${Number(val).toLocaleString()} د.ك` },
    { header: 'الكفاءة (المصروف/دخل)', accessor: 'ratio', render: val => <span className="font-bold text-orange-600">{val}</span> },
  ];

  // 3. الوحدات الشاغرة مع مدة الشغور
  const vacantUnitsData = filteredProperties.flatMap(p => 
    (p.units || []).filter(u => !u.tenantName).map(u => {
      // Logic for vacancy duration: Find last invoice for this unit or property creation
      const unitInvoices = invoices.filter(i => String(i.unitId) === String(u.id)).sort((a,b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      const lastInvoice = unitInvoices[0];
      
      const propertyCreatedAt = p.createdAt ? new Date(p.createdAt) : new Date();
      let vacatedDate = lastInvoice ? new Date(lastInvoice.date) : propertyCreatedAt;
      const diffTime = Math.abs(new Date().getTime() - vacatedDate.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return {
        propertyId: p.id,
        propertyName: p.name,
        unitNumber: u.unitNumber,
        type: u.type,
        expectedRent: u.rent,
        vacatedSince: lastInvoice ? vacatedDate.toLocaleDateString('ar-KW') : 'جديدة / لم تؤجر بعد',
        duration: diffDays
      };
    })
  );

  const vacantColumns: Column[] = [
    { header: 'العقار', accessor: 'propertyName' },
    { header: 'رقم الوحدة', accessor: 'unitNumber' },
    { header: 'النوع', accessor: 'type' },
    { header: 'تاريخ الإخلاء (تقريبي)', accessor: 'vacatedSince' },
    { header: 'مدة الشغور', accessor: 'duration', render: val => <span className="font-bold text-rose-600">{val} يوم</span> },
    { header: 'الإيجار المتوقع', accessor: 'expectedRent', render: val => `${Number(val).toLocaleString()} د.ك` }
  ];

  const customFilters = (
    <div className="flex items-center gap-2">
       <select 
          className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm"
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="All">جميع حالات العقار</option>
          <option value="نشط">نشط</option>
          <option value="تحت الصيانة">تحت الصيانة</option>
        </select>

        <select 
          className="border-slate-200 rounded-xl p-2 bg-white text-xs font-bold shadow-sm max-w-[150px]"
          value={selectedPropertyId} 
          onChange={(e) => setSelectedPropertyId(e.target.value)}
        >
          <option value="All">جميع العقارات</option>
          {properties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
    </div>
  );

  return (
    <div className="flex flex-col h-full space-y-6">
      <div className="px-6 py-4 bg-white rounded-3xl border border-slate-200 shadow-sm flex items-center justify-between">
         <div className="flex items-center gap-4">
            <span className="text-sm font-black text-slate-400">الفئة المختارة:</span>
            <div className="flex bg-slate-100 p-1 rounded-2xl">
               {(['operations', 'detailed', 'vacant'] as const).map(tab => (
                  <button
                    key={tab}
                    onClick={() => setSelectedReport(tab)}
                    className={`px-6 py-2 rounded-xl text-xs font-black transition-all ${
                        selectedReport === tab ? 'bg-white text-fly-600 shadow-md' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {tab === 'operations' ? 'نظرة عامة' : tab === 'detailed' ? 'التحليل التفصيلي' : 'الوحدات الشاغرة'}
                  </button>
               ))}
            </div>
         </div>
      </div>

      <div className="flex-1">
        {selectedReport === 'operations' && <ReportTable title="تقرير عمليات العقارات المجمّع" columns={operationsColumns} data={operationsData} customFilters={customFilters} />}
        {selectedReport === 'detailed' && <ReportTable title="تحليل كفاءة الربحية للعقارات" columns={detailedColumns} data={detailedData} customFilters={customFilters} />}
        {selectedReport === 'vacant' && <ReportTable title="سجل الوحدات الشاغرة ومدد الشغور" columns={vacantColumns} data={vacantUnitsData} customFilters={customFilters} />}
      </div>
    </div>
  );
};

export default PropertyReports;
